
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { UsersList } from "@/components/admin/UsersList";
import { Profile } from "@/hooks/profile/types";
import { PaymentStatus } from "@/hooks/admin/useUserPayments";
import { DebtInfo } from "@/hooks/admin/useMemberDebts";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface UserListContainerProps {
  currentUser: Profile | null;
  users: Profile[];
  totalUsers: number;
  filteredCount?: number;
  isLoading: boolean;
  isUpdating: boolean;
  onBanUser: (userId: string, isBanned: boolean) => void;
  onRoleChange: (userId: string, newRole: string) => void;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  paymentStatusMap?: Map<string, PaymentStatus>;
  /** True when the current user has the recepcionista role (via has_role),
   *  even if their primary profiles.role is something else (e.g. profesor). */
  actsAsRecepcionista?: boolean;
  /** MASTERGYM-only debts feature flag and per-user debt info. */
  isMastergym?: boolean;
  debtsMap?: Map<string, DebtInfo>;
}

export const UserListContainer = ({
  currentUser,
  users,
  totalUsers,
  filteredCount,
  isLoading,
  isUpdating,
  onBanUser,
  onRoleChange,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  paymentStatusMap,
  actsAsRecepcionista = false,
  isMastergym = false,
  debtsMap,
}: UserListContainerProps) => {
  const showingCount = users.length;
  const effectiveFilteredCount = filteredCount ?? totalUsers;
  const isFiltered = effectiveFilteredCount !== totalUsers;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
          <span>
            {currentUser?.role === "admin"
              ? "Todos los Usuarios del Sistema"
              : currentUser?.role === "manager"
              ? "Usuarios de mis Gimnasios"
              : "Total de Usuarios"}
          </span>
          <span className="text-sm font-normal text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>
              Mostrando {showingCount} de {totalUsers} usuarios
            </span>
            {isFiltered && (
              <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary border border-primary/30 font-medium">
                {effectiveFilteredCount} {effectiveFilteredCount === 1 ? "resultado" : "resultados"}
              </span>
            )}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((n) => (
              <Skeleton key={n} className="h-16 w-full" />
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No se encontraron usuarios con los filtros aplicados
          </div>
        ) : (
          <UsersList
            users={users}
            onBanUser={onBanUser}
            onRoleChange={onRoleChange}
            isUpdating={isUpdating}
            currentUser={currentUser}
            paymentStatusMap={paymentStatusMap}
            actsAsRecepcionista={actsAsRecepcionista}
            isMastergym={isMastergym}
            debtsMap={debtsMap}
          />
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 pt-4 border-t mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Siguiente
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
